/**
 * The citation contract every Base solution answer carries (fixed across the
 * grounded-retrieval backend and these components), plus the pure helpers the
 * chip uses. Kept free of React so it can be unit-tested directly.
 */

export interface CitationLocation {
  page?: number;
  pages?: number[];
  sheet?: string;
  /** `[first, last]` data-row ordinals. */
  rows?: [number, number];
  /** `[first, last]` line numbers. */
  lines?: [number, number];
}

export interface Citation {
  /** The `[n]` marker in the answer text. */
  n: number;
  chunk_id: string;
  index_id: string;
  document_id: string;
  display_name: string;
  location: CitationLocation;
  snippet: string;
  /** `/api/v1/indexes/{index_id}/documents/{document_id}/content` (+ `#page=N`). */
  content_url: string;
}

function span([first, last]: [number, number]): string {
  return first === last ? `${first}` : `${first}–${last}`;
}

/** Where in the document, e.g. `p.4`, `pp.4–6`, `Sheet1 › rows 3–9`, `lines 10–20`. */
export function locationLabel(loc: CitationLocation): string {
  const parts: string[] = [];
  if (loc.sheet) parts.push(loc.sheet);
  const pages = loc.pages ?? [];
  if (pages.length > 1) {
    parts.push(`pp.${span([Math.min(...pages), Math.max(...pages)])}`);
  } else if (typeof loc.page === "number") {
    parts.push(`p.${loc.page}`);
  } else if (pages.length === 1) {
    parts.push(`p.${pages[0] ?? ""}`);
  }
  if (loc.rows) parts.push(`rows ${span(loc.rows)}`);
  if (loc.lines) parts.push(`lines ${span(loc.lines)}`);
  return parts.join(" › ");
}

/** `display_name p.N` for pages; `display_name › sheet › rows a–b` otherwise. */
export function citationLabel(c: Pick<Citation, "display_name" | "location">): string {
  const where = locationLabel(c.location);
  if (!where) return c.display_name;
  const paged = !c.location.sheet && !c.location.rows && !c.location.lines;
  return paged ? `${c.display_name} ${where}` : `${c.display_name} › ${where}`;
}

/** Split `content_url` into the API path and the `#page=N` fragment (if any). */
export function splitContentUrl(url: string): { path: string; fragment: string } {
  const hash = url.indexOf("#");
  return hash === -1
    ? { path: url, fragment: "" }
    : { path: url.slice(0, hash), fragment: url.slice(hash) };
}

/** The fragment to open at, derived from the location when the URL has none. */
export function pageFragment(c: Pick<Citation, "content_url" | "location">): string {
  const { fragment } = splitContentUrl(c.content_url);
  if (fragment) return fragment;
  const page = c.location.page ?? c.location.pages?.[0];
  return typeof page === "number" ? `#page=${page}` : "";
}
